import { ComponentType, ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { Dimensions, Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import useReferralAnalytics from 'components/insights/referrals/query/useReferralAnalytics';
import { ReferralColumnKey } from 'components/insights/referrals/table/referralDetailsColumns';
import DateRangeBadge from 'components/shared/DateRangeBadge';
import Foldout from 'components/shared/Foldout';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import {
  getSlideoutEntityFilter,
  MetricDrillKey,
  useReferralsInsightsActions,
  useReferralsInsightsStore,
} from 'stores/referralsInsightsStore';

import {
  getReferralDetailsParams,
  getReferralExportParams,
  METRIC_DRILL_VIEW_BY_OPTIONS,
  SLIDEOUT_EXPORT_DIMENSION_SLUGS,
  SUB_DIVISION_DIMENSION,
  withDimensionIds,
} from '../constants';
import ReferralApiExport, { HavingCondition } from '../ReferralApiExport';
import { useReferralsInsightsContext } from '../ReferralsContainer';

import AcceptanceRateDetailHeader from './AcceptanceRateDetailHeader';
import DenialRateDetailHeader from './DenialRateDetailHeader';
import PatientListSection from './PatientListSection';
import ReferralInsightsTable from './ReferralInsightsTable';
import ResponseTimeDetailHeader from './ResponseTimeDetailHeader';
import SummaryDetailHeader from './SummaryDetailHeader';
import useSlideoutSharedData from './useSlideoutSharedData';

const METRIC_DRILL_TITLES: Record<MetricDrillKey, string> = {
  response_time: 'Avg. Response Time - Overview',
  denial_rate: 'Denial Rate - Overview',
  acceptance_rate: 'Acceptance Rate - Overview',
};

const METRIC_DRILL_EXPORT_SLUGS: Record<MetricDrillKey, string> = {
  response_time: 'response-time',
  denial_rate: 'denial-rate',
  acceptance_rate: 'acceptance-rate',
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const METRIC_DRILL_DEFAULT_SORT: Record<MetricDrillKey, { id: string; desc: boolean }> = {
  response_time: { id: 'avgResponseTime', desc: true },
  denial_rate: { id: 'deniedRate', desc: true },
  acceptance_rate: { id: 'referrals', desc: true },
};

// Server-side HAVING clauses applied to slideout queries (table and export). Each metric drill
// only shows entities that actually contributed to the metric — acceptance hides entities with
// no accepted referrals, denial hides those with no denials, response_time hides those with no
// responded referrals.
const METRIC_DRILL_HAVING: Record<MetricDrillKey, HavingCondition> = {
  response_time: { metric: Metrics.RESPONSE_TIME_SECONDS_AVERAGE, operator: 'gteq', value: 0 },
  denial_rate: { metric: Metrics.DENIAL_RATE, operator: 'gt', value: 0 },
  acceptance_rate: { metric: Metrics.ACCEPTANCE_RATE, operator: 'gt', value: 0 },
};

// Maps the table's column keys to the analytics metrics they read from. Lets the export request
// only the metrics actually shown in the current slideout view, matching CSV columns to the table.
const COLUMN_METRIC: Partial<Record<ReferralColumnKey, Metrics>> = {
  [ReferralColumnKey.REFERRALS]: Metrics.REVIEW_ID_COUNT,
  [ReferralColumnKey.UNIQUE_PATIENTS]: Metrics.LOCATION_EPISODE_ID_COUNT,
  [ReferralColumnKey.ACCEPTED_RATE]: Metrics.ACCEPTANCE_RATE,
  [ReferralColumnKey.DENIED_RATE]: Metrics.DENIAL_RATE,
  [ReferralColumnKey.CANCELED_RATE]: Metrics.CANCEL_RATE,
  [ReferralColumnKey.PENDING_RATE]: Metrics.PENDING_RATE,
  [ReferralColumnKey.AVG_RESPONSE_TIME]: Metrics.RESPONSE_TIME_SECONDS_AVERAGE,
  [ReferralColumnKey.AVG_CREATED_TO_DISCHARGE]: Metrics.CREATED_TO_DISCHARGE_DAYS_AVERAGE,
};

const METRIC_DRILL_VISIBLE_COLUMNS: Record<MetricDrillKey, readonly ReferralColumnKey[]> = {
  response_time: [
    ReferralColumnKey.ENTITY,
    ReferralColumnKey.REFERRALS,
    ReferralColumnKey.UNIQUE_PATIENTS,
    ReferralColumnKey.AVG_RESPONSE_TIME,
  ],
  denial_rate: [
    ReferralColumnKey.ENTITY,
    ReferralColumnKey.REFERRALS,
    ReferralColumnKey.UNIQUE_PATIENTS,
    ReferralColumnKey.DENIED_RATE,
  ],
  acceptance_rate: [
    ReferralColumnKey.ENTITY,
    ReferralColumnKey.REFERRALS,
    ReferralColumnKey.UNIQUE_PATIENTS,
    ReferralColumnKey.ACCEPTED_RATE,
  ],
};

type MetricHeaderProps = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

const METRIC_HEADERS: Record<MetricDrillKey, ComponentType<MetricHeaderProps>> = {
  response_time: ResponseTimeDetailHeader,
  denial_rate: DenialRateDetailHeader,
  acceptance_rate: AcceptanceRateDetailHeader,
};

function renderMetricHeader(metric: MetricDrillKey, props: MetricHeaderProps): ReactNode {
  const Header = METRIC_HEADERS[metric];
  return <Header {...props} />;
}

export default function ReferralInsightsSlideout() {
  const { sendingGroupType } = useReferralsInsightsContext();
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);
  const slideoutDimension = useReferralsInsightsStore((state) => state.slideoutDimension);
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const startDate = useReferralsInsightsStore((state) => state.filters[START_DATE]) as Date | undefined;
  const endDate = useReferralsInsightsStore((state) => state.filters[END_DATE]) as Date | undefined;
  const { closeSlideout } = useReferralsInsightsActions();

  const isMetricDrill = slideoutContext?.kind === 'metric';

  const params = useMemo(() => {
    const dim = slideoutDimension ?? Dimensions.SENDING_GROUP;
    const base = getReferralDetailsParams(dim);
    const subDim = SUB_DIVISION_DIMENSION[dim];
    const baseDims = base.dimensions ?? [];
    const dimsWithSub = subDim ? [dim, subDim, ...baseDims.slice(1)] : baseDims;
    const having = slideoutContext?.kind === 'metric' ? [METRIC_DRILL_HAVING[slideoutContext.metric]] : undefined;
    return withDimensionIds({ ...base, dimensions: dimsWithSub, ...(having ? { having } : {}) });
  }, [slideoutDimension, slideoutContext]);
  const extraFilters = useMemo(
    () => (slideoutContext ? getSlideoutEntityFilter(slideoutContext) : null),
    [slideoutContext]
  );
  const { loading, rollups } = useReferralAnalytics(params, {
    enabled: !!slideoutContext && !!slideoutDimension,
    extraFilters,
    queryKey: 'referral-slideout-analytics',
  });

  const sendingGroupTypeName = sendingGroupType?.displayName ?? 'SNF';
  const receivingGroupTypeName = selectedReceivingGroupType?.displayName ?? '';

  const isDrillingIntoSnf =
    slideoutContext?.kind === 'entity' && slideoutContext.dimension === Dimensions.SENDING_GROUP;
  const sharedData = useSlideoutSharedData();
  const { sendingCount, receivingCount, countsLoading } = sharedData;

  let title = '';
  let subtitle = '\u00A0';
  let subtitleReady = false;

  if (slideoutContext?.kind === 'entity') {
    title = `${slideoutContext.entityName} Overview`;
    subtitleReady = !countsLoading;
    subtitle = isDrillingIntoSnf
      ? `Referrals to ${receivingCount} ${receivingGroupTypeName}s`
      : `Referrals from ${sendingCount} ${sendingGroupTypeName}s`;
  } else if (slideoutContext?.kind === 'metric') {
    title = METRIC_DRILL_TITLES[slideoutContext.metric];
    subtitleReady = !countsLoading;
    subtitle = `${sendingCount} ${sendingGroupTypeName}s • ${receivingCount} ${receivingGroupTypeName}s`;
  }

  const open = slideoutContext !== null;

  const headerContent =
    slideoutContext?.kind === 'metric' ? (
      renderMetricHeader(slideoutContext.metric, { loading, rollups })
    ) : slideoutContext ? (
      <SummaryDetailHeader loading={loading} rollups={rollups} />
    ) : null;

  const tableViewByOptions = isMetricDrill ? METRIC_DRILL_VIEW_BY_OPTIONS : undefined;
  const tableDefaultSort =
    isMetricDrill && slideoutContext?.kind === 'metric' ? METRIC_DRILL_DEFAULT_SORT[slideoutContext.metric] : undefined;
  const tableVisibleColumns =
    isMetricDrill && slideoutContext?.kind === 'metric'
      ? METRIC_DRILL_VISIBLE_COLUMNS[slideoutContext.metric]
      : undefined;
  const exportHaving = useMemo(
    () => (slideoutContext?.kind === 'metric' ? [METRIC_DRILL_HAVING[slideoutContext.metric]] : undefined),
    [slideoutContext]
  );

  const exportMetrics = useMemo(() => {
    if (!tableVisibleColumns) return undefined;
    return tableVisibleColumns.map((col) => COLUMN_METRIC[col]).filter((m): m is Metrics => m != null);
  }, [tableVisibleColumns]);
  const exportParams = useMemo(
    () => (slideoutDimension ? getReferralExportParams(slideoutDimension, exportMetrics) : null),
    [slideoutDimension, exportMetrics]
  );
  const exportFilename = useMemo(() => {
    if (!slideoutContext || !slideoutDimension) return undefined;
    const viewBySlug = SLIDEOUT_EXPORT_DIMENSION_SLUGS[slideoutDimension];
    const viewBySuffix = viewBySlug ? `-${viewBySlug}` : '';
    if (slideoutContext.kind === 'metric') {
      return `referral-coordination-${METRIC_DRILL_EXPORT_SLUGS[slideoutContext.metric]}${viewBySuffix}`;
    }
    return `referral-coordination-${slugify(slideoutContext.entityName)}${viewBySuffix}`;
  }, [slideoutContext, slideoutDimension]);

  return (
    <Foldout open={open} onBackdropClick={closeSlideout} width='min(95%, 960px)'>
      <Container>
        <HeaderBar>
          <TitleSection>
            <TitleRow>
              <Title>{title}</Title>
              {startDate && endDate && <DateRangeBadge startDate={startDate} endDate={endDate} />}
            </TitleRow>
            <Subtitle>{slideoutContext && subtitleReady ? subtitle : '\u00A0'}</Subtitle>
          </TitleSection>
          <HeaderActions>
            {exportParams && (
              <ReferralApiExport
                params={exportParams}
                filename={exportFilename}
                extraFilters={extraFilters}
                having={exportHaving}
                disabled={loading}
              />
            )}
            <CloseButton type='button' aria-label='Close' onClick={closeSlideout}>
              {'×'}
            </CloseButton>
          </HeaderActions>
        </HeaderBar>
        <Body>
          {slideoutContext && (
            <>
              {headerContent}
              <BorderedSection>
                <ReferralInsightsTable
                  key={
                    slideoutContext.kind === 'metric'
                      ? `metric:${slideoutContext.metric}`
                      : `entity:${slideoutContext.entityId}`
                  }
                  loading={loading}
                  rollups={rollups}
                  visibleColumns={tableVisibleColumns}
                  viewByOptions={tableViewByOptions}
                  defaultSort={tableDefaultSort}
                />
              </BorderedSection>
              <PatientListSection />
            </>
          )}
        </Body>
      </Container>
    </Foldout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.black};
`;

const Subtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black50};
  letter-spacing: 0.18px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.black75};
  padding: 4px 8px;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BorderedSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 24px;
`;
